import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTenant, api } from '../../lib/api';
import { useRole } from '../../lib/role';
import PipelineProgressBar from '../../components/PipelineProgressBar';
import CfDnsCard from '../../components/CfDnsCard';
import SigninFailureCard from '../../components/SigninFailureCard';
import SequencerConnectionsCard from '../../components/SequencerConnectionsCard';
import PendingSwapCard from '../../components/PendingSwapCard';
import RenameMailboxesCard from '../../components/RenameMailboxesCard';

export default function Overview() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data: t } = useTenant(id);
  const { isAdmin } = useRole();
  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectMsg, setReconnectMsg] = useState<string | null>(null);
  const [creds, setCreds] = useState<{
    ga_upn: string; ga_password: string | null; initial_password: string | null;
    totp_code: string | null; totp_seconds_remaining: number | null;
  } | null>(null);
  const [credsBusy, setCredsBusy] = useState(false);
  const [credsErr, setCredsErr] = useState<string | null>(null);
  const [totpCountdown, setTotpCountdown] = useState<number | null>(null);
  const [updatePwOpen, setUpdatePwOpen] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [updateBusy, setUpdateBusy] = useState(false);
  const [updateMsg, setUpdateMsg] = useState<string | null>(null);
  const [updateErr, setUpdateErr] = useState<string | null>(null);

  async function handleUpdatePw(e: React.FormEvent) {
    e.preventDefault();
    if (!newPw.trim()) return;
    setUpdateBusy(true); setUpdateMsg(null); setUpdateErr(null);
    try {
      await api.updateTenantCredentials(id, { ga_password: newPw });
      setUpdateMsg('Stored. Bootstrap re-enqueued — MS sign-in retry with the new password.');
      setNewPw('');
      if (creds) await fetchCreds();  // refresh the shown value
      qc.invalidateQueries({ queryKey: ['tenant', id] });
    } catch (e: any) {
      setUpdateErr(e?.message || 'Update failed.');
    } finally {
      setUpdateBusy(false);
    }
  }

  async function fetchCreds() {
    setCredsBusy(true); setCredsErr(null);
    try {
      const c = await api.revealTenantCredentials(id);
      setCreds(c);
      setTotpCountdown(c.totp_seconds_remaining);
    } catch (e: any) {
      setCredsErr(e?.message || 'Failed to reveal — needs admin cookie session.');
    } finally {
      setCredsBusy(false);
    }
  }

  async function handleRevealCreds() {
    if (creds) { setCreds(null); setTotpCountdown(null); return; }
    await fetchCreds();
  }

  // TOTP countdown: tick every second, auto-refetch when the current
  // 30s window expires so the code stays live without user action.
  useEffect(() => {
    if (!creds || creds.totp_code == null) return;
    const iv = setInterval(() => {
      setTotpCountdown(prev => {
        if (prev == null) return prev;
        if (prev <= 1) {
          fetchCreds();
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creds?.totp_code]);
  if (!t) return null;
  const mc = t.mailbox_counts || {};

  const totalMb = Number(mc.total ?? 0);
  const errCount = Number(mc.errors ?? mc.oauth_error ?? 0);
  // "Re-run connector" only makes sense after the pipeline has exited
  // (done / warning / failed). Hiding it while running/queued avoids
  // double-enqueueing OAuth on top of an in-flight run.
  const hasExited =
    t.status === 'done' || t.status === 'warning' || t.status === 'failed';
  const showReconnect = hasExited && totalMb > 0 && errCount > 0;

  async function handleReconnect() {
    setReconnecting(true); setReconnectMsg(null);
    try {
      const out = await api.reoauthFailed(id);
      const n = out?.detail?.count ?? out?.detail?.reoauth ?? 0;
      setReconnectMsg(
        n > 0
          ? `Re-running connector for ${n} mailbox${n === 1 ? '' : 'es'}…`
          : 'Nothing to reconnect — every mailbox is already connected.',
      );
      qc.invalidateQueries({ queryKey: ['tenant', id] });
      qc.invalidateQueries({ queryKey: ['mailboxes', id] });
    } catch (e: any) {
      setReconnectMsg(`Failed to enqueue: ${e?.message || e}`);
    } finally {
      setReconnecting(false);
    }
  }

  return (
    <div className="p-6 max-w-5xl space-y-4">
      <SigninFailureCard tenant={t} />
      <PendingSwapCard tenant={t} />
      <CfDnsCard tenant={t} />
      <PipelineProgressBar tenant={t} simple={!isAdmin} />
      <SequencerConnectionsCard tenant={t} />
      <div className="grid grid-cols-3 gap-4">
        <Card title="Identity">
          <Row k="Tenant login" v={t.ga_upn} />
          <Row k="Domain" v={t.target_domain} />
          {isAdmin && <Row k="Azure tenant" v={t.azure_tenant_id || '—'} />}
          <div className="pt-2 border-t border-line mt-2">
            <button
              onClick={handleRevealCreds}
              disabled={credsBusy}
              className="text-xs text-accent hover:underline disabled:opacity-50"
            >
              {credsBusy ? 'Loading…' : creds ? 'Hide password' : 'Reveal admin password'}
            </button>
            {credsErr && <div className="text-err text-[11px] mt-1">{credsErr}</div>}
            {creds && (
              <div className="mt-2 space-y-1">
                <Row k="Current password" v={
                  <span className="font-mono text-xs select-all">{creds.ga_password || '—'}</span>
                } />
                {creds.initial_password && creds.initial_password !== creds.ga_password && (
                  <Row k="Initial password" v={
                    <span className="font-mono text-xs select-all">{creds.initial_password}</span>
                  } />
                )}
                <Row k="Update password" v={
                  <button
                    onClick={() => { setUpdatePwOpen(v => !v); setUpdateMsg(null); setUpdateErr(null); }}
                    className="text-xs text-accent hover:underline"
                  >
                    {updatePwOpen ? 'Cancel' : 'Set a new value'}
                  </button>
                } />
                {updatePwOpen && (
                  <form onSubmit={handleUpdatePw} className="flex items-center gap-2 mt-1">
                    <input
                      type="text"
                      value={newPw}
                      onChange={e => setNewPw(e.target.value)}
                      placeholder="password MS currently accepts"
                      className="input font-mono text-xs flex-1"
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={updateBusy || !newPw.trim()}
                      className="btn-primary text-xs disabled:opacity-50"
                    >
                      {updateBusy ? 'Saving…' : 'Save'}
                    </button>
                  </form>
                )}
                {updateMsg && <div className="text-ok text-[11px]">{updateMsg}</div>}
                {updateErr && <div className="text-err text-[11px]">{updateErr}</div>}
                <Row k="MFA code" v={
                  creds.totp_code ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="font-mono text-sm tracking-widest select-all">
                        {creds.totp_code.slice(0, 3)} {creds.totp_code.slice(3)}
                      </span>
                      <span className="text-[10px] text-dim tabular-nums">
                        {totpCountdown != null ? `${totpCountdown}s` : '…'}
                      </span>
                    </span>
                  ) : (
                    <span className="text-dim text-[11px]">no TOTP enrolled</span>
                  )
                } />
              </div>
            )}
          </div>
        </Card>
        <Card title="Mailboxes">
          <Row k="Created" v={`${mc.created ?? 0} / ${mc.total ?? 0}`} />
          {isAdmin && <Row k="Password set" v={`${mc.password_set ?? 0}`} />}
          {isAdmin && <Row k="Tenant login flipped" v={`${mc.flipped ?? 0}`} />}
        </Card>
        <Card title="OAuth">
          {/* Non-admin responses use 'connected' / 'errors'; admins keep the
              raw oauth_done / oauth_error keys for parity with the rest of
              the debug surface. */}
          <Row k="Connected" v={`${(mc.connected ?? mc.oauth_done) ?? 0} / ${mc.total ?? 0}`} />
          <Row k="Errors" v={`${(mc.errors ?? mc.oauth_error) ?? 0}`} />
        </Card>
      </div>
      {showReconnect && (
        <div className="card p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <div className="text-sm text-ink font-medium">
              {errCount} mailbox{errCount === 1 ? '' : 'es'} didn't connect
            </div>
            <div className="text-xs text-dim mt-0.5">
              Re-running the connector retries OAuth for just the failed
              mailboxes. The successfully connected ones aren't touched.
            </div>
          </div>
          <button
            onClick={handleReconnect}
            disabled={reconnecting}
            className="btn-primary disabled:opacity-50"
          >
            {reconnecting ? 'Re-running…' : 'Re-run connector'}
          </button>
        </div>
      )}
      {reconnectMsg && (
        <div className="text-xs text-dim">{reconnectMsg}</div>
      )}
      <RenameMailboxesCard tenant={t} />
      {t.status !== 'done' && (
        <div className="flex gap-2 items-center">
          <button
            onClick={async () => {
              try { await api.runPipeline(id); }
              catch (e: any) { alert(e.message || 'failed to enqueue'); }
            }}
            disabled={t.status === 'running' || t.status === 'queued' || t.status === 'rerun_queued'}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Run full pipeline
          </button>
          {(t.status === 'running' || t.status === 'queued' || t.status === 'rerun_queued') && (
            <span className="text-xs text-dim">already running — wait for it to finish or fail</span>
          )}
        </div>
      )}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="text-xs text-dim mb-2">{title}</div>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <div className="flex justify-between gap-2"><span className="text-dim">{k}</span><span className="truncate">{v}</span></div>;
}
